/**
 * Video Service - CloudFront Video URL Management
 *
 * Handles fetching signed URLs for private videos and direct URLs for public videos.
 * Private videos require authentication and use CloudFront signed URLs.
 * Public videos are accessible directly via CloudFront.
 *
 * Created: January 3, 2026
 */

import axios from 'axios';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const API_URL = process.env.VUE_APP_TEAMIO_API_URL;
const VIDEO_CDN_URL = process.env.VUE_APP_VIDEO_CDN_URL || 'https://videos.teamio.app';

/**
 * Wait for Firebase auth state to be ready
 * This handles the case where auth state hasn't been restored yet
 */
function waitForAuth() {
  return new Promise((resolve, reject) => {
    const auth = getAuth();

    // If user is already available, resolve immediately
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }

    // Wait for auth state to be restored
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      if (user) {
        resolve(user);
      } else {
        reject(new Error('User not authenticated'));
      }
    }, (error) => {
      unsubscribe();
      reject(error);
    });

    // Timeout after 10 seconds
    setTimeout(() => {
      unsubscribe();
      reject(new Error('Authentication timeout - please try again'));
    }, 10000);
  });
}

export const videoService = {
  /**
   * Get a signed URL for a private video
   * Requires user to be authenticated via Firebase
   *
   * @param {string} key - Video path (e.g., 'private/tutorials/intro.mp4')
   * @returns {Promise<{success: boolean, url: string, expiresIn: number}>}
   * @throws {Error} If not authenticated or request fails
   */
  async getSignedUrl(key) {
    // Wait for auth state to be ready
    const user = await waitForAuth();

    // Get fresh ID token from Firebase
    const token = await user.getIdToken();

    const response = await axios.get(`${API_URL}/video/signed-url`, {
      params: { key },
      headers: { Authorization: `Bearer ${token}` }
    });

    return response.data;
  },

  /**
   * Get public video URL (no signing required)
   * These videos are accessible to anyone via CloudFront
   *
   * @param {string} key - Video path (e.g., 'public/marketing/demo.mp4')
   * @returns {Promise<{success: boolean, url: string}>}
   */
  async getPublicUrl(key) {
    const response = await axios.get(`${API_URL}/video/public-url`, {
      params: { key }
    });

    return response.data;
  },

  /**
   * Get direct public URL without API call
   * Use this for known public videos to avoid extra API roundtrip
   *
   * @param {string} key - Video path (e.g., 'public/marketing/demo.mp4')
   * @returns {string} Direct CloudFront URL
   */
  getPublicUrlDirect(key) {
    // Ensure key doesn't have leading slash
    const cleanKey = key.startsWith('/') ? key.slice(1) : key;
    return `${VIDEO_CDN_URL}/${cleanKey}`;
  }
};

export default videoService;
